/**
 * login.tsx — Login-Route
 *
 * Login-Formular mit React Hook Form + Zod.
 * Devise/JWT authentication via POST /api/v2/auth/login.
 * Demo-Modus bleibt via Demo-Start-Button erhalten.
 */
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import './login.css'
import { useAuthStore } from '../stores/authStore'
import { useDemoMode } from '../hooks/useDemoMode'
import { queryClient } from '../lib/query-client'

// i18n-Fix (AUTH-Block): Zod-Fehlertexte kamen vormals hartkodiert auf Deutsch.
// Schema wird innerhalb der Komponente mit t() gebaut (analog register.tsx).
function buildLoginSchema(t: (key: string) => string) {
  return z.object({
    email: z.string().email(t('validation.invalidEmail')),
    password: z.string().min(1, t('validation.passwordEmpty')),
  })
}

type LoginInput = z.infer<ReturnType<typeof buildLoginSchema>>

const LOGIN_URL = `${import.meta.env.VITE_API_BASE_URL}/api/v2/auth/login`

type LoginSearch = { registered?: boolean; passwordReset?: boolean }

export const Route = createFileRoute('/login')({
  // Rueckgabetyp mit optionalen Feldern (statt immer-boolean), damit bestehende
  // navigate({ to: '/login' })-Aufrufe im Rest der App (ohne search) weiterhin
  // typchecken — TanStack Router leitet "required search" sonst aus dem
  // konkreten Rueckgabetyp ab.
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    // Erfolgs-Feedback nach Registrierung (AUTH-Block-Fix, statt stummem Redirect)
    registered: search.registered === true || search.registered === 'true' ? true : undefined,
    // Erfolgs-Feedback nach Passwort-Reset (reset-password.tsx, AUTH-Block-Fix)
    passwordReset:
      search.passwordReset === true || search.passwordReset === 'true' ? true : undefined,
  }),
  beforeLoad: () => {
    if (useAuthStore.getState().isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const { t } = useTranslation('auth')
  const { activate } = useDemoMode()
  const navigate = useNavigate()
  const setTokens = useAuthStore((s) => s.setTokens)
  const { registered, passwordReset } = Route.useSearch()
  const [formError, setFormError] = useState<string | null>(null)
  // STAGING-FIX 2026-09-02: Checkbox war bislang folgenlos (defaultChecked,
  // aber nie ausgewertet) — steuert jetzt ob der Refresh-Token zusaetzlich
  // in localStorage gespiegelt wird (siehe authStore.setTokens).
  const [rememberMe, setRememberMe] = useState(true)

  const loginSchema = useMemo(() => buildLoginSchema(t), [t])

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  })

  function handleStartDemo() {
    // Ohne konfigurierten VITE_DEMO_API_TOKEN bleibt der Demo-Modus aus —
    // dann nicht navigieren, sondern Hinweis zeigen.
    if (!activate()) {
      setFormError(t('login.demoUnavailable'))
      return
    }
    void navigate({ to: '/dashboard' })
  }

  async function onSubmit(data: LoginInput) {
    setFormError(null)
    try {
      const res = await fetch(LOGIN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
        }),
      })

      if (!res.ok) {
        // 500 != falsches Passwort (ACCEPTANCE.md): Serverfehler duerfen nicht
        // als Anmeldedaten-Fehler ausgegeben werden, sonst verdaechtigt der
        // User faelschlich sein eigenes Passwort statt eines Backend-Ausfalls.
        if (res.status >= 500) {
          setFormError(t('login.errors.unexpectedError'))
          return
        }
        setValue('password', '')
        setFormError(t('login.errors.invalidCredentials'))
        setFocus('password')
        return
      }

      const json = (await res.json()) as {
        token?: string
        refresh_token?: string
        expires_in?: number
      }

      if (!json.token) {
        setFormError(t('login.errors.unexpectedError'))
        return
      }

      // Token in Store (Memory) + sessionStorage (Refresh-Fallback)
      // FE-ROB-07: expires_in des Backends durchreichen. Ohne den dritten
      // Parameter fiel der Store auf DEFAULT_ACCESS_TOKEN_TTL_SECONDS (7200s)
      // zurueck — bei kuerzer konfiguriertem Doorkeeper hielt
      // isAccessTokenFresh() den Token faelschlich fuer gueltig, der proaktive
      // Refresh-Guard griff nicht und jeder Request lief erst in einen 401
      // samt Retry (Latenz-Verdopplung).
      setTokens(json.token, json.refresh_token, json.expires_in, rememberMe)

      // QueryClient leeren — frischer State nach Login
      queryClient.clear()

      void navigate({ to: '/dashboard' })
    } catch {
      setFormError(t('login.errors.networkError'))
    }
  }

  return (
    <div className="auth-shell" data-testid="login-page">
      {/* Linke Spalte: Formular */}
      <div className="auth-form-wrap">
        <div className="auth-form">
          {/* Logo-Mark */}
          <div className="row" style={{ gap: 10 }}>
            <div className="logo-mark">H</div>
            <div style={{ fontWeight: 600 }}>HeliumTracker</div>
          </div>

          <div>
            <h1 style={{ fontSize: 28 }}>{t('login.title')}</h1>
            <p className="muted" style={{ margin: '6px 0 0' }}>
              {t('login.subtitle')}
            </p>
          </div>

          {registered && (
            <div
              role="status"
              aria-live="polite"
              className="alert alert-success"
              data-testid="registration-success-message"
            >
              {t('register.successMessage')}
            </div>
          )}

          {passwordReset && (
            <div
              role="status"
              aria-live="polite"
              className="alert alert-success"
              data-testid="password-reset-success-message"
            >
              {t('resetPassword.successMessage')}
            </div>
          )}

          {formError && (
            <div
              role="alert"
              aria-live="polite"
              className="alert alert-error"
              data-testid="form-error"
            >
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate aria-label={t('login.formAriaLabel')} style={{ display: 'contents' }}>
            <div>
              <label className="label" htmlFor="email">{t('login.emailLabel')}</label>
              <input
                id="email"
                type="email"
                className="input"
                aria-label={t('login.emailAriaLabel')}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
                {...register('email')}
              />
              {errors.email && (
                <span
                  id="email-error"
                  role="alert"
                  aria-live="polite"
                  className="field-error"
                  data-testid="email-error"
                >
                  {errors.email.message}
                </span>
              )}
            </div>

            <div>
              <div className="spread" style={{ marginBottom: 6 }}>
                <label className="label" htmlFor="password" style={{ margin: 0 }}>{t('login.passwordLabel')}</label>
                <a href="/forgot-password" style={{ fontSize: 12, color: 'var(--primary)' }}>
                  {t('login.forgotLink')}
                </a>
              </div>
              <input
                id="password"
                type="password"
                className="input"
                aria-label={t('login.passwordAriaLabel')}
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? 'password-error' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <span
                  id="password-error"
                  role="alert"
                  aria-live="polite"
                  className="field-error"
                  data-testid="password-error"
                >
                  {errors.password.message}
                </span>
              )}
            </div>

            <label
              className="row"
              htmlFor="stay-signed-in"
              style={{ gap: 6, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}
            >
              <input
                id="stay-signed-in"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ accentColor: 'var(--primary)' }}
              />
              {t('login.staySignedIn')}
            </label>

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={isSubmitting}
              aria-label={t('login.submitAriaLabel')}
              style={{ width: '100%' }}
              data-testid="submit-button"
            >
              {isSubmitting ? t('login.submitting') : t('login.submitLabel')}
            </button>
          </form>

          <div className="auth-divider">{t('login.divider')}</div>

          <button
            className="btn btn-ghost"
            onClick={handleStartDemo}
            data-testid="demo-start-button"
            type="button"
            style={{ width: '100%' }}
          >
            {t('login.demoButton')}
          </button>

          <div className="muted" style={{ fontSize: 13, textAlign: 'center' }}>
            {t('login.noAccount')}{' '}
            <a href="/register" style={{ color: 'var(--primary)' }}>
              {t('login.registerLink')}
            </a>
          </div>

          <div className="muted" style={{ fontSize: 11, textAlign: 'center', marginTop: 6 }}>
            {t('login.footnote')}
          </div>
        </div>
      </div>

      {/* Rechte Spalte: Branding-Aside */}
      <aside className="auth-aside">
        <div className="row" style={{ gap: 10 }}>
          <div className="logo-mark">H</div>
          <div style={{ fontWeight: 600 }}>HeliumTracker</div>
        </div>

        <div>
          <h2 style={{ fontSize: 26, lineHeight: 1.25 }}>
            {t('aside.loginHeadline')}
          </h2>
          <p className="muted" style={{ marginTop: 12, fontSize: 14, maxWidth: 420, lineHeight: 1.6, opacity: 0.85 }}>
            {t('aside.loginDescription')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, marginTop: 22 }}>
            <div className="row" style={{ gap: 10 }}><Check size={16} aria-hidden="true" /> {t('aside.feature1')}</div>
            <div className="row" style={{ gap: 10 }}><Check size={16} aria-hidden="true" /> {t('aside.feature2')}</div>
            <div className="row" style={{ gap: 10 }}><Check size={16} aria-hidden="true" /> {t('aside.feature3')}</div>
          </div>
        </div>

        <div className="muted" style={{ fontSize: 11 }}>
          {t('aside.copyright')} · <a href="/privacy">{t('aside.privacyLink')}</a>
        </div>
      </aside>
    </div>
  )
}
